import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/AppShell";
import { spouseOf } from "@/lib/household";
import { syncDates } from "@/lib/fers";
import { ContribModeToggle } from "@/components/ContribInput";

const FREQ = [["biweekly", "Biweekly"], ["semimonthly", "Semi-monthly"], ["monthly", "Monthly"]];

function SpouseContrib({ s, set }) {
  const fixed = s.contrib_mode === "fixed";
  const k = fixed ? "contrib_value" : "contrib_pct";
  return (
    <div className="col-span-2 sm:col-span-4 lg:col-span-7 flex flex-wrap items-end gap-3 border-t border-border/60 pt-3" data-testid="spouse-contrib">
      <div className="space-y-1.5"><Label className="text-xs">TSP contribution type</Label><div><ContribModeToggle mode={fixed ? "fixed" : "percent"} onChange={(m) => set("contrib_mode", m)} testid="spouse-contrib-mode" /></div></div>
      <div className="space-y-1.5 w-36">
        <Label className="text-xs">{fixed ? "$ per paycheck" : "TSP contribution %"}</Label>
        <Input type="number" min={0} className="font-mono-num" value={s[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} data-testid="spouse-contrib-value-input" />
      </div>
      {fixed && (
        <div className="space-y-1.5 w-40">
          <Label className="text-xs">Pay schedule</Label>
          <Select value={s.pay_frequency || "biweekly"} onValueChange={(v) => set("pay_frequency", v)}>
            <SelectTrigger data-testid="spouse-pay-frequency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FREQ.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

const NUM_FIELDS = [
  ["age", "Age"], ["birth_year", "Birth year"], ["retire_age", "Retirement age"], ["life_expectancy", "Life expectancy"],
  ["salary", "Salary ($/yr)"], ["service_years", "Service so far (yrs)"], ["roth_split", "Roth share %"],
  ["tsp_balance", "TSP balance"], ["trad_ira", "Traditional IRA"], ["roth_ira", "Roth IRA"], ["ss_pia_override", "SS at FRA ($/mo)"], ["ss_claim_age", "SS claim age"], ["high3_override", "High-3 override"],
];

export function SpousePanel({ p, setP }) {
  const s = spouseOf(p);
  const set = (k, v) => setP({ ...p, spouse: syncDates({ ...s, [k]: v }) });
  return (
    <Panel title="Spouse / partner" testid="panel-spouse"
      action={<div className="flex items-center gap-2"><Label htmlFor="sp-on" className="text-xs">Include in household plan</Label><Switch id="sp-on" checked={!!s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="spouse-enable-switch" /></div>}>
      {s.enabled ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="spouse-fields">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={s.name} onChange={(e) => set("name", e.target.value)} data-testid="spouse-name-input" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">System</Label>
            <Select value={s.retirement_system} onValueChange={(v) => set("retirement_system", v)}>
              <SelectTrigger data-testid="spouse-system-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[["FERS", "FERS"], ["CSRS", "CSRS"], ["BRS", "Military BRS"], ["civilian", "Civilian / none"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {[["birth_date", "Birth date"], ["service_start_date", "Service comp. date"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{l}</Label>
              <Input type="date" className="font-mono-num" value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`spouse-${k}-input`} />
            </div>
          ))}
          {NUM_FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{l}</Label>
              <Input type="number" className="font-mono-num" value={s[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`spouse-${k}-input`} />
            </div>
          ))}
          <SpouseContrib s={s} set={set} />
        </div>
      ) : <p className="text-sm text-muted-foreground">Turn on to add a spouse's TSP, pension and Social Security and see one household paycheck.</p>}
    </Panel>
  );
}
